import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  Checkbox,
} from '@mui/material';
import { useResourceContext } from 'react-admin';
import {
  ChangeEvent,
  MouseEvent,
  memo,
  useState,
  useContext,
  useMemo,
  Fragment,
  useCallback,
  useRef,
} from 'react';

import { useCosmos } from 'providers/contexts/CosmosContext';
import { bloxValues } from 'themes/constants';
import { MMM_DD_YYYY_C_12HHMM } from 'utility/constants';
import { getPrimaryBgColor, getSolidThemeGray } from 'utility/styles';
import {
  cleanStringFromHtmlAndBloxInline,
  formatAssetString,
  formatDate,
  isAssetTypeEditorEnabled,
} from 'utility/functions';
import {
  useNxtImport,
  NxtImportItem,
} from 'components/ExportImport/NxtImport/NxtImportContext';
import { WireSearchContext } from 'screens/syndication/views/search/syndication/WireSearchWithContext';
import SearchLinkWrapper from './SearchLinkWrapper';
import { useSearch } from '../SearchContextProvider';
import { UserAvatar } from '../../UserAvatar/UserAvatar';
import { IconHelper } from '../../generic/IconHelper';
import { canSelectSearchItem } from '../utility/searchItemSelection';
import {
  GenericSearchItem,
  getRecordRealId,
  useSearchItems,
} from './SearchItemsProvider';
import {
  MiniMenuSelector,
  TMenuState,
  TSetMenuEvent,
} from 'components/Minimenus/MiniMenuSelector';
import { IconAndStatusSwitcher } from './elements/IconAndStatusSwitcher';
import SearchMediaPreview from './fields/generic/SearchMediaPreview';
import { useTranslation } from 'react-i18next';
import { isRowSelected } from 'utility/selectionModelUtils';

type SearchCardProps = {
  record: GenericSearchItem;
};

const SearchCard = memo(({ record }: SearchCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { sessionConfig } = useCosmos();
  const resource = useResourceContext();
  const { getSearchItemInfo, isModalSearch } = useSearch();
  const {
    setSelectedItems,
    selectedItems,
    selectedItemIds,
    selectItem,
    deselectById,
    clearSelectedItems,
    hasSelectionEnabled,
    hasMiniMenuEnabled,
    miniMenuConfig,
    miniMenuHandlers,
  } = useSearchItems();
  // hover states for styles
  const [cardHover, setCardHover] = useState<boolean>(false);
  // mouse context event state for right click
  const [menuEvent, setMenuEvent] = useState<TMenuState>(null);
  // anchor element for button-triggered mini menu
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // --- Data Formatting ---
  const siteTimeZone = sessionConfig?.time_zone;
  const type = record?.type || '';
  const title = record?.title || '';
  const summary = record?.summary;
  const content = record?.content;
  const previews = record?.previews; // syndication
  const image_preview = record?.image_preview; // editorial
  const start_time = record?.start_time;
  const authors = record?.authors;
  const cleanSummary = cleanStringFromHtmlAndBloxInline(summary || content || '');
  const fmtStartTime = formatDate({
    date: start_time,
    shiftTz: siteTimeZone,
    format: MMM_DD_YYYY_C_12HHMM,
  });
  const author = authors?.[0];
  const previewImageUrl =
    image_preview?.url || previews?.thumbnail?.url || previews?.size1?.url;
  const typeFormatted = type ? formatAssetString(type, t) : type;

  // --- Logic ---

  const recordId = getRecordRealId(record, resource);
  const searchItemInfo = useMemo(() => {
    return getSearchItemInfo(record);
  }, [getSearchItemInfo, record]);

  const toUrl = searchItemInfo?.url || undefined;
  const toTarget = searchItemInfo?.target || undefined;
  const routingEnabled = searchItemInfo?.disabled !== true;

  const editorEnabled = isAssetTypeEditorEnabled(type);
  const isCardSelected = useMemo(() => {
    if (!recordId) return false;
    return isRowSelected(selectedItemIds, recordId);
  }, [selectedItemIds, recordId]);

  // --- Themes / Styles ---
  const isDarkMode = theme.palette.mode === 'dark';
  const baseBgColor = isDarkMode
    ? theme.palette.grey[900]
    : theme.palette.background.paper;
  const greyBgSolid = isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100];
  const hoverDividerColor = isDarkMode
    ? theme.palette.primary.dark
    : theme.palette.primary.light;
  const bgHoverSolid = getPrimaryBgColor(theme, true);
  const dividerColor = theme.palette.divider;
  const transitionSx = theme.transitions.create([
    'background-color',
    'box-shadow',
    'border-color',
  ]);

  // --- Event Handlers ---
  const onCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (!recordId) {
      console.warn('No recordId, unable to select/deselect.');
      return;
    }
    // Prevent selection of disabled items (only in modal mode)
    if (!canSelectSearchItem(searchItemInfo, isModalSearch)) {
      return;
    }
    const checked = e.target.checked;
    if (checked) {
      selectItem(record);
    } else {
      deselectById(recordId);
    }
  };

  // prevent navigation on checkbox click
  const onCheckboxClick = (e: MouseEvent) => {
    e.stopPropagation();
  };
  // handle card click for modal search to select/deselect
  const onCardClick = useCallback(() => {
    if (!recordId) {
      return;
    }
    // Prevent selection of disabled items (only in modal mode)
    if (!canSelectSearchItem(searchItemInfo, isModalSearch)) {
      return;
    }
    if (isCardSelected) {
      deselectById(recordId);
    } else {
      selectItem(record);
    }
  }, [
    record,
    recordId,
    isCardSelected,
    selectItem,
    deselectById,
    searchItemInfo,
    isModalSearch,
  ]);

  // minimenu handlers
  const handleMiniMenuClose = useCallback(() => {
    setMenuEvent(null);
    setAnchorEl(null);
  }, []);

  const handleActionsButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setMenuEvent(null);
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleContextMenu: TSetMenuEvent = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (!hasSelectionEnabled && !hasMiniMenuEnabled) {
        if (menuEvent) setMenuEvent(null); // clear menu if was already open somehow
        if (anchorEl) setAnchorEl(null);
        return; // no menu if selection is disabled
      }
      // Show menu without selecting the item - just pass the record
      setAnchorEl(null);
      setMenuEvent(event);
    },
    [menuEvent, anchorEl, hasSelectionEnabled, hasMiniMenuEnabled],
  );

  return (
    <Fragment>
      <MiniMenuSelector
        miniProps={{
          raResource: resource,
          menuEvent,
          setMenuEvent,
          handleMiniMenuClose,
          clearSelectedItems,
          setSelectedItems,
          selectedItems,
          contextMenuItem: record, // Pass the right-clicked item
          isContextItemDisabled: searchItemInfo?.disabled, // Pass disabled state
          configMap: miniMenuConfig,
          anchorEl,
          ...miniMenuHandlers,
        }}
      />
      <SearchLinkWrapper
        onContextMenu={handleContextMenu}
        record={record}
        to={toUrl}
        target={toTarget}
      >
        <Card
          ref={cardRef} // Needed for video hover play.
          className="editorial-search-card"
          data-testid="editorial-search-card"
          onMouseEnter={() => {
            setCardHover(true);
          }}
          onMouseLeave={() => {
            setCardHover(false);
          }}
          onClick={isModalSearch ? onCardClick : undefined}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: transitionSx,
            outline: `2px solid ${
              isCardSelected ? theme.palette.primary.light : getSolidThemeGray(theme)
            }`,
            // Apply disabled styling (opacity and visual feedback)
            ...(searchItemInfo?.disabled && {
              opacity: 0.7,
              color: theme.palette.text.secondary,
            }),
            '& .MuiAvatar-root.MuiAvatar-circular': {
              opacity: 1,
              transition: 'opacity 0.2s',
            },
            '& .play-icon-overlay': {
              opacity: 1,
              transition: 'opacity 0.2s',
            },
            '& .video-expand-icon-overlay': {
              opacity: 0,
              transition: 'opacity 0.2s',
            },
            '&:hover': {
              backgroundColor: bgHoverSolid,
              cursor: isModalSearch ? 'pointer' : routingEnabled ? 'pointer' : 'default',
              '& #card-content': {
                backgroundColor: routingEnabled ? bgHoverSolid : greyBgSolid,
                boxShadow: routingEnabled
                  ? `0px 0px 0px 1px ${hoverDividerColor}`
                  : `0px 0px 0px 1px ${dividerColor}`,
                transition: transitionSx,
              },
              '& #card-mediatype-stack': {
                backgroundColor: routingEnabled ? bgHoverSolid : greyBgSolid,
                borderTop: routingEnabled
                  ? `1px solid ${hoverDividerColor}`
                  : `1px solid ${dividerColor}`,
                borderRight: routingEnabled
                  ? `1px solid ${hoverDividerColor}`
                  : `1px solid ${dividerColor}`,
                '&::before': {
                  backgroundColor: routingEnabled ? bgHoverSolid : greyBgSolid,
                  boxShadow: routingEnabled
                    ? `0px 1px 0px 0px ${bgHoverSolid}`
                    : `0px 1px 0px 0px ${greyBgSolid}`,
                },
                transition: transitionSx,
              },
              '& #card-media-box': {
                backgroundColor: greyBgSolid,
                transition: transitionSx,
              },
              '& .play-icon-overlay': {
                opacity: 0,
              },
              '& .video-expand-icon-overlay': {
                opacity: 1,
              },
            },
          }}
        >
          <Box
            id="card-media-box"
            data-testid="card-media-box"
            sx={{
              height: 230,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: greyBgSolid,
              transition: transitionSx,
            }}
          >
            {hasSelectionEnabled && (
              <Checkbox
                id="checkbox-select-card"
                data-testid="checkbox-select-card"
                checked={isCardSelected}
                onChange={onCheckboxChange}
                onClick={onCheckboxClick}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  display: selectedItemIds.ids.size || cardHover ? undefined : 'none',
                  zIndex: 500,
                  borderRadius: '50%',
                  color: theme.palette.action.active,
                  '&.MuiCheckbox-root': {
                    backgroundColor:
                      selectedItemIds.ids.size || cardHover
                        ? 'rgba(144, 144, 144, 0.25)'
                        : undefined,
                    '&.Mui-checked': {
                      color: theme.palette.primary.light,
                      backgroundColor: 'rgba(144, 144, 144, 0.25)',
                      '&:hover': {
                        backgroundColor: 'rgba(144, 144, 144, 0.25)',
                      },
                    },
                  },
                }}
              />
            )}
            <SearchMediaPreview
              record={record}
              previewImageUrl={previewImageUrl}
              view="card"
              isHovered={cardHover}
            />
            <UserAvatar
              user={author}
              size={40}
              placement="top"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                boxShadow: theme.shadows[1],
              }}
            />
          </Box>
          <CardContent
            id="card-content"
            data-testid="card-content"
            sx={{
              '&.MuiCardContent-root': {
                p: 1,
                '&:last-child': {
                  pb: 1,
                },
              },
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              justifyContent: 'space-between',
              position: 'relative',
              boxShadow: `0px 0px 0px 1px ${dividerColor}`, // border, works better than actual border for the curved effect
              backgroundColor: baseBgColor,
              transition: transitionSx,
            }}
          >
            <Stack
              id="card-mediatype-stack"
              data-testid="card-mediatype-stack"
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'absolute',
                top: -28,
                left: 0,
                pr: '8px',
                pl: '7px',
                pb: '0px',
                pt: '4px',
                borderTopRightRadius: '5px',
                borderTop: `1px solid ${dividerColor}`,
                borderRight: `1px solid ${dividerColor}`,
                backgroundColor: baseBgColor,
                transition: transitionSx,
                // pseudo element paper color to mask the border(boxShadow)
                // on the card-content and 2px for subpixel rendering coverage
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 25,
                  left: 0,
                  width: '100%',
                  height: '2px',
                  backgroundColor: baseBgColor,
                  boxShadow: `0px 1px 0px 0px ${baseBgColor}`,
                  transition: transitionSx,
                },
              }}
            >
              <IconHelper
                iconType={type}
                svgIconProps={{
                  fontSize: 'small',
                  sx: {
                    zIndex: 1, // above the pseudo element
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: 'small',
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  textTransform: 'capitalize',
                  pl: '2px',
                }}
              >
                {typeFormatted}
              </Typography>
            </Stack>
            <Stack
              id="card-text-stack"
              data-testid="card-text-stack"
              direction="column"
              sx={{
                gap: '10px',
              }}
            >
              <Box
                id="card-title-box"
                data-testid="card-title-box"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1 / 2,
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: bloxValues.xlFontSizeRem,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'normal',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                  }}
                >
                  {title}
                </Typography>
                {hasMiniMenuEnabled && (
                  <Tooltip title={t('blox.general.more_actions', 'More actions')}>
                    <IconButton
                      size="small"
                      aria-label={t('blox.general.more_actions', 'More actions')}
                      onClick={handleActionsButtonClick}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: bloxValues.regularFontSizeRem,
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'normal',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3,
                }}
              >
                {cleanSummary}
              </Typography>
            </Stack>
            <Stack
              id="card-footer-col"
              direction="column"
              sx={{
                pt: '10px', // easier scanning of text
                gap: '5px', // if editor not enabled
              }}
            >
              {!editorEnabled && (
                <Typography
                  sx={{
                    color: isDarkMode
                      ? theme.palette.error.dark
                      : theme.palette.error.main,
                    fontSize: bloxValues.tinyFontSizeRem,
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'normal',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  Editing {type} assets is not currently supported.
                </Typography>
              )}
              <Stack
                id="starttime-and-status-apart"
                data-testid="starttime-and-status-apart"
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Tooltip
                  id="date-tooltip"
                  data-testid="date-tooltip"
                  disableInteractive
                  title="Start Time"
                  arrow={false}
                  placement="top-start"
                  enterDelay={200}
                  slotProps={{
                    tooltip: {
                      sx: {
                        px: '6px',
                        py: '2px',
                      },
                    },

                    popper: {
                      popperOptions: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -12],
                            },
                          },
                        ],
                      },
                    },
                  }}
                >
                  <Typography
                    id="date-text"
                    data-testid="date-text"
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: bloxValues.smallFontSizeRem,
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {fmtStartTime}
                  </Typography>
                </Tooltip>
                <Typography
                  id="status-text"
                  data-testid="status-text"
                  sx={{
                    textTransform: 'capitalize',
                    fontSize: bloxValues.smallFontSizeRem,
                    color: theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1 / 2,
                  }}
                >
                  <IconAndStatusSwitcher
                    record={record}
                    resource={resource}
                  />
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </SearchLinkWrapper>
    </Fragment>
  );
});
SearchCard.displayName = 'SearchCard';

/**
 * Resolves a display status string from an NxtImportItem's state flags.
 */
function resolveImportStatus(importItem: NxtImportItem): string | null {
  if (importItem.isProcessing || importItem.isQueued || importItem.isUploading) {
    return 'importing';
  }
  if (importItem.isFailed) return 'import_error';
  if (importItem.isReady) return 'imported';
  return null;
}

const SearchCardView = () => {
  const resource = useResourceContext();
  const { cleanData } = useSearchItems();
  const { hasSyndicationImports, imports } = useNxtImport();
  const wireSearchContext = useContext(WireSearchContext);

  // Derive card data by merging syndication import statuses into cleanData
  const cardData = useMemo(() => {
    if (!cleanData?.length) return cleanData;
    if (resource !== 'wire/channel/search' || !hasSyndicationImports) return cleanData;

    const currentChannel = wireSearchContext?.selectedChannel;
    if (!currentChannel) return cleanData;

    const importsForChannel = Object.values(imports).filter(
      (imp) => imp.resourceType === 'wire' && imp.importId.startsWith(currentChannel),
    );
    if (!importsForChannel.length) return cleanData;

    // Build lookup: object_id -> import item
    const importByObjectId = new Map<string, NxtImportItem>();
    for (const imp of importsForChannel) {
      const objectId = imp.importId.split(':').slice(1).join(':');
      importByObjectId.set(objectId, imp);
    }

    return cleanData.map((row) => {
      const importItem = importByObjectId.get(row.object_id);
      if (!importItem) return row;
      return {
        ...row,
        status: resolveImportStatus(importItem),
        asset_uuid: importItem.uuid,
      };
    });
  }, [
    cleanData,
    resource,
    hasSyndicationImports,
    wireSearchContext?.selectedChannel,
    imports,
  ]);

  return (
    <Box
      sx={{
        gap: 2,
        display: 'grid',
        justifyContent: 'flex-start',
        gridTemplateColumns: 'repeat(auto-fill, minmax(345px, 1fr))',
        p: 2,
      }}
    >
      {cardData.length === 0 ? (
        <Typography>No results found.</Typography>
      ) : (
        cardData.map((record) => (
          <SearchCard
            key={record?.id}
            record={record}
          />
        ))
      )}
    </Box>
  );
};

export default SearchCardView;
